export type TileCellOffset = { px: number; py: number };

/**
 * 0..1 spacing → ~0..100px in preview space; multiply by `scale` when exporting at another resolution.
 */
export function spacingPxFromNormalized(spacingNormalized: number, scale = 1): number {
  const n = Math.max(0, Math.min(1, Number(spacingNormalized) || 0));
  return n * 100 * Math.max(0, scale);
}

/** Repeats a composite tile (content inset by spacing on all sides), like BitmapShader REPEAT. */
export function buildCompositeTileCellOffsets(
  imageWidth: number,
  imageHeight: number,
  contentWidth: number,
  contentHeight: number,
  spacingPx: number,
  rotation: number,
  maxCells = 2000
): TileCellOffset[] {
  const W = Math.max(1, imageWidth);
  const H = Math.max(1, imageHeight);

  const compositeW = Math.max(1, contentWidth + spacingPx * 2);
  const compositeH = Math.max(1, contentHeight + spacingPx * 2);

  const isRotated = rotation !== 0;

  const diag = Math.sqrt(W * W + H * H);
  const effectiveW = isRotated ? diag : W;
  const effectiveH = isRotated ? diag : H;

  const cols = Math.ceil(effectiveW / compositeW) + 4;
  const rows = Math.ceil(effectiveH / compositeH) + 4;

  const startC = isRotated ? -Math.floor(cols / 2) : -1;
  const startR = isRotated ? -Math.floor(rows / 2) : -1;

  const out: TileCellOffset[] = [];

  for (let r = startR; r < rows && out.length < maxCells; r++) {
    for (let c = startC; c < cols && out.length < maxCells; c++) {
      out.push({
        px: c * compositeW + spacingPx,
        py: r * compositeH + spacingPx,
      });
    }
  }

  return out;
}
